<template>
    <div class="flex items-center gap-2">
        <BaseButton
            :theme="value === 1 ? 'secondary' : 'neutral'"
            size="sm"
            icon-only
            data-testid="feedback-thumb-up"
            :aria-pressed="value === 1"
            :aria-label="t('insights.feedback.thumbsUp')"
            :disabled="disabled"
            @click="handleThumbUp">
            <i class="bi bi-hand-thumbs-up" aria-hidden="true"></i>
        </BaseButton>
        <BasePopover
            v-model:open="showFeedbackPopover"
            as-child
            side="top"
            :side-offset="8"
            :collision-padding="8"
            align="end"
            content-class="max-w-md w-full max-h-(--reka-popover-content-available-height) overflow-y-auto p-4">
            <template #trigger>
                <BaseButton
                    :theme="value === 0 ? 'secondary' : 'neutral'"
                    size="sm"
                    icon-only
                    data-testid="feedback-thumb-down"
                    :aria-pressed="value === 0"
                    :aria-label="t('insights.feedback.thumbsDown')"
                    :disabled="disabled"
                    @click="handleThumbDown">
                    <i class="bi bi-hand-thumbs-down" aria-hidden="true"></i>
                </BaseButton>
            </template>
            <template #content>
                <FeedbackIssuesForm
                    target="insight"
                    :initial-comment="initialComment ?? undefined"
                    :initial-selected-issues="initialIssues ?? undefined"
                    @save="handleFeedbackFormSave"
                    @dismiss="closePopover" />
            </template>
        </BasePopover>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BasePopover from "~vue/components/BasePopover.vue";
import FeedbackIssuesForm from "~vue/Feedback/FeedbackIssuesForm.vue";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

interface Props {
    value: number | null;
    disabled?: boolean;
    initialComment?: string | null;
    initialIssues?: string[] | null;
}

const props = withDefaults(defineProps<Props>(), {
    value: null,
    disabled: false,
    initialComment: null,
    initialIssues: null,
});

const emit = defineEmits<{
    select: [value: 0 | 1];
    negativeFeedback: [payload: { comment?: string | null; issues?: string[] | null }];
}>();

const showFeedbackPopover = ref(false);

function closePopover() {
    showFeedbackPopover.value = false;
}

function handleThumbUp() {
    if (props.disabled) {
        return;
    }
    closePopover();
    if (props.value !== 1) {
        emit("select", 1);
    }
}

function handleThumbDown() {
    if (props.disabled) {
        return;
    }
    // Save thumbs down immediately with no comment/issues, then show popover for optional details
    if (props.value !== 0) {
        emit("select", 0);
    }
    showFeedbackPopover.value = true;
}

function handleFeedbackFormSave(payload: { issues: string[] | null; comments: string | null }) {
    emit("negativeFeedback", {
        comment: payload.comments ?? null,
        issues: payload.issues ?? null,
    });
    closePopover();
}
</script>
